import React, { useMemo, useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  orderSelectors,
  getOrderList,
  setStatusFilter,
  setDateFilter,
  exportOrders,
  setSearchTerm,
} from 'redux/slices/order';
import { fundraiserSelectors } from 'redux/slices/fundraiser';
import { currentCompanySelector } from 'redux/slices/company';

import WarningPanel from 'components/WarningPanel';
import DashboardContainer from 'components/DashboardContainer';
import CardWithAction from 'components/CardWithAction';
import OrderTable from 'components/OrderTable';
import { orderList as TEXT } from 'locales/en';
import PATHS from 'routes/paths';
import { usePrevious, useDebounce } from 'hooks';
import { logEvent, logCtaWithLabel, logActionRequired } from 'utils/ga';

const OrderList = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [hasTableLoader, setHasTableLoader] = useState(false);

  const orderList = useSelector(orderSelectors.selectAll);
  const fundraisersCount = useSelector(fundraiserSelectors.selectTotal);
  const {
    loading: fetchingOrders,
    pagination,
    search_term: searchTerm,
    status_filter: statusFilter,
    date_filter: dateFilter,
  } = useSelector((state) => state.order);

  const storeLoading = useSelector((state) => state.javelinStore.loading);
  const companyLoading = useSelector((state) => state.company.loading);
  const displayFullPageLoader =
    storeLoading || companyLoading || (!hasTableLoader && fetchingOrders);

  const hasLiveFundraisers = useSelector(
    (state) => state.fundraiser.hasLiveFundraisers
  );
  const currentCompany = useSelector(currentCompanySelector);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const prevSearchTerm = usePrevious(searchTerm, '');
  const promiseRef = useRef(null);

  const hasOrders = useMemo(
    () =>
      !!orderList.length ||
      !!statusFilter ||
      !!debouncedSearchTerm ||
      hasTableLoader,
    [orderList.length, statusFilter, debouncedSearchTerm, hasTableLoader]
  );

  useEffect(() => {
    if (!debouncedSearchTerm) return;
    if (debouncedSearchTerm.length >= 3) {
      if (promiseRef.current?.abort) {
        promiseRef.current.abort();
      }
      setHasTableLoader(true);
      promiseRef.current = dispatch(getOrderList()).then(() => {
        logEvent('search', {
          search_term: debouncedSearchTerm,
        });
        setHasTableLoader(false);
      });
    }
  }, [debouncedSearchTerm, dispatch]);

  useEffect(() => {
    // when delete search term
    if (prevSearchTerm !== searchTerm && searchTerm.length === 0) {
      setHasTableLoader(true);
      dispatch(getOrderList()).then(() => {
        setHasTableLoader(false);
      });
    }
  }, [dispatch, prevSearchTerm, searchTerm]);

  const displayPanel = useMemo(() => {
    if (!currentCompany.account_holder_verifications?.length) {
      return false;
    }
    return currentCompany.account_holder_verifications.some(
      (item) =>
        ['FAILED', 'INVALID_DATA', 'AWAITING_DATA'].indexOf(item.status) > -1
    );
  }, [currentCompany.account_holder_verifications]);

  const renderView = useMemo(() => {
    const onChangeDateFilter = async (item) => {
      if (dateFilter.value !== item.value) {
        logEvent('click', {
          category: 'date_filter',
          label: item.value,
        });
        await dispatch(setDateFilter(item));
        setHasTableLoader(true);
        dispatch(getOrderList()).then(() => setHasTableLoader(false));
      }
    };

    const onSearch = (event) => {
      dispatch(setSearchTerm(event.target.value || ''));
    };

    const onChangeStatusFilter = async (item) => {
      if (statusFilter?.value !== item?.value || !item) {
        setHasTableLoader(true);
        if (!!item) {
          logEvent('click', {
            category: 'status_filter',
            label: item.value,
          });
          await dispatch(setStatusFilter(item));
          dispatch(getOrderList()).then(() => setHasTableLoader(false));
        } else {
          dispatch(setStatusFilter(null));
          dispatch(getOrderList()).then(() => {
            setHasTableLoader(false);
          });
        }
      }
    };

    const onConfirmExport = () => {
      setHasTableLoader(true);
      return dispatch(exportOrders()).then(() => {
        setHasTableLoader(false);
      });
    };

    return (
      <OrderTable
        data={orderList}
        onChangeStatusFilter={onChangeStatusFilter}
        onSearch={onSearch}
        onChangeDateFilter={onChangeDateFilter}
        onConfirmExport={onConfirmExport}
        displayLoader={hasTableLoader}
        pagination={pagination}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        dateFilter={dateFilter}
      />
    );
  }, [
    dateFilter,
    dispatch,
    hasTableLoader,
    orderList,
    pagination,
    searchTerm,
    statusFilter,
  ]);

  const header = useMemo(() => {
    if (fundraisersCount === 0) {
      return TEXT.noFundraiserHeader1;
    } else if (!hasOrders) {
      return TEXT.emptyHeader1;
    } else {
      return TEXT.header1;
    }
  }, [fundraisersCount, hasOrders]);

  const subheader = useMemo(() => {
    if (fundraisersCount === 0) {
      return TEXT.noFundraiserHeader2;
    } else if (!hasLiveFundraisers) {
      return TEXT.noLiveHeader2;
    } else if (!hasOrders) {
      return TEXT.emptyHeader2;
    } else {
      return TEXT.header2;
    }
  }, [fundraisersCount, hasLiveFundraisers, hasOrders]);

  return (
    <DashboardContainer
      header={header}
      subheader={subheader}
      fullPageLoading={displayFullPageLoader}
      transitionIn={!displayFullPageLoader}
      transitionAppear={displayFullPageLoader}
    >
      {displayPanel && (
        <WarningPanel
          testId="error-panel"
          onClick={() => {
            logActionRequired();
            history.push(PATHS.home);
          }}
          text={TEXT.panelHeader}
          text2={TEXT.panelText}
        />
      )}
      {!hasOrders && (
        <CardWithAction
          title={!fundraisersCount ? TEXT.createFirst : TEXT.create}
          subtitle={TEXT.createFirstSubtitle}
          cta={TEXT.go}
          to={PATHS.fundraisers}
          callback={() => logCtaWithLabel('go_to_fundraisers')}
          className="flex flex-col lg:flex-row lg:h-40 items-center text-center lg:text-left lg:mb-0"
        />
      )}
      {hasOrders && (
        <div className="w-full lg:overflow-auto lg:shadow-md lg:border-transparent">
          {renderView}
        </div>
      )}
    </DashboardContainer>
  );
};

export default OrderList;
